import React, { useEffect, useMemo } from 'react';
import { Link, Outlet } from 'react-router-dom';

import { BasePage } from '../BasePage';
import { CreatePartialConfigMenu } from './CreatePartialConfigMenu';
import { SwarmTypeTranslations } from '../../../enums';
import { format } from '../../../utils/formatting';
import {
    PARTIAL_CONFIG_TYPE_LABELS,
    createPartialConfigEditUrl,
    isPartialConfigTypeLabelKey,
    isSwarmType,
} from './utils';
import { usePartialConfigOverview } from './queries';
import Table from '../../table/Table';
import Button from '../../form/Button';
import { DEFAULT_PAGE_SIZE } from '../../table/hooks';

type PartialConfigTableRow = {
    id: string;
    name: string;
    editUrl: string;
    swarmLabel: string;
    configTypeLabel: string;
    updatedAtLabel: string;
    description: string;
    scope: string;
    usageLabel: string;
};

export function OverviewPage() {
    const { nodes, hasNextPage, isInitialLoading, isFetchingMore, loadMore, error } =
        usePartialConfigOverview({
            pageSize: DEFAULT_PAGE_SIZE,
        });

    useEffect(() => {
        if (!isInitialLoading && hasNextPage && !isFetchingMore) {
            void loadMore();
        }
    }, [hasNextPage, isFetchingMore, isInitialLoading, loadMore]);

    const tableRows = useMemo<PartialConfigTableRow[]>(
        () =>
            nodes.map((config) => {
                const partialConfigTypeValue =
                    typeof config.partialConfigType === 'string'
                        ? config.partialConfigType
                        : String(config.partialConfigType ?? '');
                const configTypeLabel =
                    partialConfigTypeValue && isPartialConfigTypeLabelKey(partialConfigTypeValue)
                        ? PARTIAL_CONFIG_TYPE_LABELS[partialConfigTypeValue]
                        : partialConfigTypeValue;
                const editUrl = createPartialConfigEditUrl(
                    config.id,
                    config.swarmType,
                    config.partialConfigType
                );
                const updatedAt = new Date(String(config.modifiedAt));
                const updatedAtLabel = Number.isNaN(updatedAt.getTime())
                    ? String(config.modifiedAt ?? '')
                    : format(updatedAt, {
                          useBrowserTimezone: true,
                      });
                const swarmLabel = isSwarmType(config.swarmType)
                    ? SwarmTypeTranslations[config.swarmType]
                    : gettext('UNKNOWN_SWARM');

                return {
                    id: config.id,
                    name: config.name ?? gettext('UNTITLED_CONFIGURATION'),
                    editUrl,
                    swarmLabel,
                    configTypeLabel,
                    updatedAtLabel,
                    description: gettext('DESCRIPTION_COMING_SOON'),
                    scope: gettext('PROJECT'),
                    usageLabel: gettext('IN_USE'),
                };
            }),
        [nodes]
    );

    const columns = useMemo(
        () => [
            {
                key: 'name',
                title: gettext('CONFIGURATION_NAME'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <div className="flex flex-col">
                        <Link to={row.editUrl} className="text-sm font-semibold hover:underline">
                            {row.name}
                        </Link>
                        <span className="text-xs text-gray-500">
                            {(() => {
                                const label = gettext('UPDATED_AT_LABEL');
                                return label.includes('%s')
                                    ? label.replace('%s', row.updatedAtLabel)
                                    : `${label} ${row.updatedAtLabel}`;
                            })()}
                        </span>
                    </div>
                ),
            },
            {
                key: 'swarmLabel',
                title: gettext('SWARM'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <span className="text-sm text-gray-800">{row.swarmLabel}</span>
                ),
            },
            {
                key: 'configTypeLabel',
                title: gettext('CONFIG_TYPE'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <span className="text-sm text-gray-800">{row.configTypeLabel}</span>
                ),
            },
            {
                key: 'description',
                title: gettext('DESCRIPTION'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <span className="text-sm text-gray-500">{row.description}</span>
                ),
            },
            {
                key: 'scope',
                title: gettext('SCOPE'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <span className="text-sm text-gray-800">{row.scope}</span>
                ),
            },
            {
                key: 'usageLabel',
                title: gettext('USAGE'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-green-300 px-3 py-1 text-xs font-semibold text-green-600">
                        {row.usageLabel}
                    </span>
                ),
            },
            {
                key: 'actions',
                title: gettext('ACTIONS'),
                render: (_column: unknown, row: PartialConfigTableRow) => (
                    <Button
                        to={row.editUrl}
                        variant="primary"
                        className="inline-flex px-6 py-2 text-sm"
                    >
                        {gettext('VIEW')}
                    </Button>
                ),
            },
        ],
        []
    );

    function renderTable() {
        if (error) {
            return (
                <p className="text-sm text-red-600">
                    {gettext('UNABLE_TO_LOAD_PARTIAL_CONFIGURATIONS')}
                </p>
            );
        }

        if (!isInitialLoading && tableRows.length === 0) {
            return <p className="text-gray-500">{gettext('No data found')}</p>;
        }

        return (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-800">
                        {gettext('ALL_PARTIAL_CONFIGURATIONS')}
                    </h2>
                </div>
                <Table
                    data={tableRows}
                    columns={columns}
                    loading={isInitialLoading}
                    showDivider={false}
                    showBorder={false}
                    wrapperClasses="rounded-b-2xl overflow-x-scroll"
                    headerRowClasses="bg-gray-50 text-left text-xs font-semibold tracking-wide text-gray-500"
                    headerCellClasses="px-6 py-3"
                    rowClasses="border-t border-gray-100"
                    rowCellClasses="px-6 py-4 text-sm text-gray-800"
                />
            </div>
        );
    }

    return (
        <BasePage
            title={gettext('PARTIAL_CONFIGURATIONS')}
            besideTitle={<CreatePartialConfigMenu />}
        >
            <div className="space-y-8">
                <div className="space-y-6">{renderTable()}</div>
                <Outlet />
            </div>
        </BasePage>
    );
}
